import { inject, Injectable } from '@angular/core';
import { RecaptchaService } from '@services/recaptcha-service';
import { TurnstileService } from '@services/turnstile-service';

export interface OtpChallengeParams {
  recaptcha?: { action: string; token: string };
  turnstile?: { token: string };
  // Index signature so this typed bag is assignable to the connection-service
  // `Record<string, unknown>` OTP params (a typed variable, unlike an object literal,
  // otherwise isn't index-compatible).
  [key: string]: unknown;
}

/**
 * Resolves the bot-protection tokens to attach to an OTP request. Always runs both reCAPTCHA and
 * Turnstile and attaches whatever tokens they yield — the client no longer consults server Config
 * to decide which challenge to run. The server silently ignores a challenge it has disabled, so
 * sending both is safe.
 */
@Injectable({ providedIn: 'root' })
export class BotProtectionService {
  private readonly recaptcha: RecaptchaService = inject(RecaptchaService);
  private readonly turnstile: TurnstileService = inject(TurnstileService);

  /**
   * @param action           challenge action name (e.g. 'requestOTP', 'retryOTP').
   * @param includeRecaptcha whether reCAPTCHA applies to this flow. The server's OTP-retry path
   *                         only verifies Turnstile, so the resend flow passes `false`.
   */
  async getOtpChallengeParams(action: string, includeRecaptcha: boolean = true): Promise<OtpChallengeParams> {
    const params: OtpChallengeParams = {};

    if (includeRecaptcha) {
      params.recaptcha = { action, token: await this.recaptcha.execute(action) };
    }
    const turnstileToken: string | null = await this.turnstile.execute(action);
    if (turnstileToken) params.turnstile = { token: turnstileToken };
    return params;
  }
}
